import { Checkbox, FormControlLabel, Radio } from "@mui/material";
import { PropsWithChildren, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import {
	Menu,
	MenuItemContentWrapper,
	MenuItemListWrapper,
	MenuItemWrapper,
	StyledChip,
	WarningAmber
} from "components/OrderSearch/chipDropdownMenu.styles";
import { FilterType } from "./filterType";
import CancelIcon from "@mui/icons-material/Cancel";
import { filterSpecificBrands } from "utils/order";
import ChipDropdownMenuSearch from "./ChipDropdownMenuSearch";
import ChipDropdownMenuSearchNoResults from "./ChipDropdownMenuSearchNoResults";

const ChipDropDownMenu = ({
	children,
	selectedItems = [],
	filterType,
	items,
	onChange,
	hasWarning,
	isSearchable,
	GTMIdentifier,
	dataTestId
}: ChipDropdownMenuProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const anchorRef = useRef<HTMLDivElement>(null);
	const hasSelections = selectedItems.length > 0;
	const getItemLabel = useCallback(
		(itemValue: string) => {
			return filterType === FilterType.ORDER_TYPE ? itemValue : filterSpecificBrands(itemValue);
		},
		[filterType]
	);

	const handleClear = () => {
		if (filterType !== FilterType.SORTED_BY) {
			onChange([]);
			defaultSearchItems();
		}
	};

	const handleItemOnChange = (event: SetStateAction<any>) => {
		if (filterType === FilterType.SORTED_BY) {
			onChange([event.target.value]);
		} else {
			const itemWasSelected = !selectedItems.includes(event.target.value);

			const updatedItems = itemWasSelected
				? [...selectedItems, event.target.value]
				: selectedItems.filter((item) => item !== event.target.value);

			onChange(updatedItems);
		}
	};

	const getLabel = () => {
		if (filterType === FilterType.SORTED_BY) {
			return `Sort By: ${selectedItems[0]}`;
		}

		if (selectedItems.length === 0 || items?.length === selectedItems.length) {
			return filterType.toString();
		}

		if (selectedItems.length === 1) {
			return getItemLabel(selectedItems[0] ?? "");
		}

		return filterType.toString() + " (" + selectedItems.length + ")";
	};
	const label = getLabel();

	const [searchedItems, setSearchedItems] = useState(items);
	const [isSearched, setIsSearched] = useState(false);
	const [searchValue, setSearchValue] = useState<string>("");

	useEffect(() => {
		setSearchedItems(items);
	}, [items]);

	const handleOnClose = () => {
		setIsOpen((prev) => !prev);
		if (selectedItems.length === 0) {
			defaultSearchItems();
		}
	};

	const defaultSearchItems = useCallback(() => {
		setSearchedItems(items);
		setIsSearched(false);
		setSearchValue("");
	}, [items]);

	const handleSearch = useCallback(
		(query: string | undefined) => {
			if (query?.trim()) {
				const normalize = (str: string) => str.toUpperCase();
				const filterItems = (array: (string | ItemType)[] | undefined, searchTerm: string): string[] => {
					return (
						array
							?.map((item) => (typeof item === "string" ? item : item.value))
							?.filter((value) => normalize(value).includes(normalize(searchTerm.trim()))) ?? []
					);
				};
				const searchResults = filterItems(items, query);
				setSearchedItems(searchResults);
				setIsSearched(true);
				setSearchValue(query);
			} else {
				defaultSearchItems();
			}
		},
		[items, defaultSearchItems]
	);

	const getItemValue = (item: string | ItemType): string =>
		item && typeof item !== "string" ? (item.value ?? "") : (item ?? "");

	return (
		<div>
			<StyledChip
				hasWarning={hasWarning}
				clickable
				ref={anchorRef}
				deleteIcon={
					hasSelections && filterType !== FilterType.SORTED_BY ? (
						<div data-testid="CancelIcon">
							<CancelIcon />
						</div>
					) : undefined
				}
				onDelete={hasSelections && filterType !== FilterType.SORTED_BY ? handleClear : undefined}
				variant="outlined"
				label={label}
				onClick={() => setIsOpen(!isOpen)}
				icon={<ExpandMoreRounded />}
				color={hasSelections ? "secondary" : "primary"}
				data-testid={dataTestId}
				data-id={GTMIdentifier}
			/>
			<Menu
				open={isOpen}
				anchorEl={anchorRef.current}
				onClose={handleOnClose}
				data-testid="filter-chip-dropdown-menu"
			>
				{isSearchable && (
					<ChipDropdownMenuSearch
						filterType={filterType}
						handleSearch={handleSearch}
						cancelSearch={defaultSearchItems}
						isSearched={isSearched}
						value={searchValue}
					/>
				)}
				{children}
				<div>
					{isSearched && searchedItems?.length === 0 ? (
						<ChipDropdownMenuSearchNoResults filterType={filterType} />
					) : (
						<MenuItemListWrapper
							isSearchable={isSearchable}
							child={children}
						>
							{searchedItems?.map((item) => {
								const itemValue = getItemValue(item);
								const itemError = typeof item !== "string" && item?.error;

								return (
									<MenuItemWrapper key={JSON.stringify(item)}>
										<MenuItemContentWrapper item={item}>
											<FormControlLabel
												data-testid="filter-chip-dropdown-menu-option"
												control={
													filterType === FilterType.SORTED_BY ? (
														<Radio
															value={itemValue}
															size="medium"
															checked={selectedItems.includes(itemValue)}
															onChange={handleItemOnChange}
														/>
													) : (
														<Checkbox
															value={itemValue}
															size="medium"
															checked={selectedItems.includes(itemValue)}
															onChange={handleItemOnChange}
														/>
													)
												}
												label={getItemLabel(itemValue)}
											/>
											{itemError && <WarningAmber fontSize="small" />}
										</MenuItemContentWrapper>
									</MenuItemWrapper>
								);
							})}
						</MenuItemListWrapper>
					)}
				</div>
			</Menu>
		</div>
	);
};

export interface ChipDropdownMenuProps extends PropsWithChildren {
	filterType: FilterType;
	items: string[] | ItemType[] | undefined;
	onChange: (arg: string[]) => void;
	selectedItems?: string[];
	hasWarning?: boolean;
	isSearchable?: boolean;
	GTMIdentifier: string;
	dataTestId?: string;
}

export interface ItemType {
	value: string;
	error: boolean;
}

export default ChipDropDownMenu;
